/*
 * Copyright (c) 2017 Datarista, Inc.
 */

/* eslint-disable consistent-return */

import { polyfill } from 'es6-promise';
import { push } from 'react-router-redux';
import { handleError, startAction, completeAction, reportSuccess } from './common';
import { accountServiceClient } from '../utils/api';

polyfill();

export const actionTypes = {
  GET_INSTALLS_SUCCESS: 'GET_INSTALLS_SUCCESS',
  SET_INSTALL_SUCCESS: 'SET_INSTALL_SUCCESS',
  GET_INSTALL_USERS_SUCCESS: 'GET_INSTALL_USERS_SUCCESS',
  GET_INSTALL_META_SUCCESS: 'GET_INSTALL_META_SUCCESS',
  INVITE_INSTANCE_USER_SUCCESS: 'INVITE_INSTANCE_USER_SUCCESS',
  UPDATE_ACCOUNT_USER_SUCCESS: 'UPDATE_ACCOUNT_USER_SUCCESS',
  UPDATE_PASSWORD_SUCCESS: 'UPDATE_PASSWORD_SUCCESS',
  UPDATE_PLATFORM_MARKETO_CONFIG_SUCCESS: 'UPDATE_PLATFORM_MARKETO_CONFIG_SUCCESS',
  GET_PLATFORM_MARKETO_CONFIG_SUCCESS: 'GET_PLATFORM_MARKETO_CONFIG_SUCCESS',
  UPDATE_PLATFORM_ELOQUA_CONFIG_SUCCESS: 'UPDATE_PLATFORM_ELOQUA_CONFIG_SUCCESS',
  GET_PLATFORM_ELOQUA_CONFIG_SUCCESS: 'GET_PLATFORM_ELOQUA_CONFIG_SUCCESS',
};

// Connect to the API
const swaggerApi = accountServiceClient();

export function receiveUsers(users, installId) {
  return {
    type: actionTypes.GET_INSTALL_USERS_SUCCESS,
    users,
    installId,
  };
}

// Get installation users
export function getInstallationUsers(installId, token) {
  const actionText = 'Getting Installation Users';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.portal
        .getInstallationUsers({
          Authorization: `bearer ${token}`,
          installId,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const users = response.obj;
            dispatch(receiveUsers(users, installId));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

export function updatePasswordSuccess(response) {
  return {
    type: actionTypes.UPDATE_PASSWORD_SUCCESS,
    response,
  };
}

export function updateUserPassword(body, token) {
  const actionText = 'Updating User Password';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.auth
        .updatePassword({
          body,
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            reportSuccess(dispatch, 'Password Updated.');
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

export function updateUserSuccess(user) {
  return {
    type: actionTypes.UPDATE_ACCOUNT_USER_SUCCESS,
    user,
  };
}

export function updateUserAccountSettings(user, token) {
  const actionText = 'Updating User Account Settings';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.portal
        .updatePortalUser({
          body: user,
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            reportSuccess(dispatch, 'User Updated.');
            const userObj = response.obj;
            dispatch(updateUserSuccess(userObj));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

export function updateInstallUser(user, installationId, token) {
  const actionText = 'Updates Install User';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.portal
        .updateInstallUser({
          body: user,
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            // reportSuccess(dispatch, 'User Deleted.');
            dispatch(getInstallationUsers(installationId, token));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

export function returnHome() {
  return dispatch => {
    dispatch(push('/home'));
  };
}

// Remove a user from the install instance
export function deleteInstallUser(user, installationId, token) {
  const actionText = 'Removing Install User';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.portal
        .deleteInstallUser({
          body: user,
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            reportSuccess(dispatch, 'User Deleted.');
            // Refresh user list
            dispatch(getInstallationUsers(installationId, token));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

// Add and invite users to an install instance (Admins only)
export function inviteInstallUser(installationId, token, email, permission) {
  const actionText = 'Inviting New Install User';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.portal
        .inviteUser({
          installationId,
          body: {
            email,
            permission,
          },
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            reportSuccess(dispatch, 'New User Added. Invitation Email Sent.');
            // Refresh user list
            dispatch(getInstallationUsers(installationId, token));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

export function receiveInstalls(installsList, instanceId) {
  return {
    type: actionTypes.GET_INSTALLS_SUCCESS,
    installsList,
    instanceId,
  };
}

// Retrieve a list of Instances for current user
export function getInstalls(token, instanceId) {
  const actionText = 'Getting User Installations';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.portal
        .getInstalls({
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const installsList = response.obj;
            dispatch(receiveInstalls(installsList, instanceId));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

export function setInstall(installObj) {
  return {
    type: actionTypes.SET_INSTALL_SUCCESS,
    installObj,
  };
}

// If we already have the current installs, set the currentInstal
export function setCurrentInstall(installObj) {
  const actionText = 'Setting Current Installation';
  return dispatch => {
    startAction(dispatch, actionText);
    dispatch(setInstall(installObj));
    completeAction(dispatch, actionText);
  };
}

export function updateMarketoConfigSuccess(config) {
  return {
    type: actionTypes.UPDATE_PLATFORM_MARKETO_CONFIG_SUCCESS,
    config,
  };
}

// Set install options for Marketo integration
export function updateMarketoConfig(data, installId, token) {
  const actionText = 'Updating Marketo Install Configuration Options';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.portal
        .updateMarketoConfig({
          installId,
          body: data,
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            reportSuccess(dispatch, 'Credentials Have Been Verified with Marketo and Saved.');
            const marketoData = response.obj;
            dispatch(updateMarketoConfigSuccess(marketoData));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

export function getMarketoConfigSuccess(config) {
  return {
    type: actionTypes.GET_PLATFORM_MARKETO_CONFIG_SUCCESS,
    config,
  };
}

// Get install options for Marketo integration
export function getMarketoConfig(installId, token) {
  const actionText = 'Getting Marketo Install Configuration Options';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.portal
        .getMarketoConfig({
          installId,
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const marketoData = response.obj;
            dispatch(getMarketoConfigSuccess(marketoData));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

export function updateEloquaConfigSuccess(config) {
  return {
    type: actionTypes.UPDATE_PLATFORM_ELOQUA_CONFIG_SUCCESS,
    config,
  };
}

// Set install options for Eloqua integration
export function updateEloquaConfig(data, installId, token) {
  const actionText = 'Updating Eloqua Install Configuration Options';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.portal
        .updateEloquaConfig({
          installId,
          body: data,
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            reportSuccess(dispatch, 'Eloqua Configuration Updated.');
            const eloquaData = response.obj;
            dispatch(updateEloquaConfigSuccess(eloquaData));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

export function getEloquaConfigSuccess(config) {
  return {
    type: actionTypes.GET_PLATFORM_ELOQUA_CONFIG_SUCCESS,
    config,
  };
}

// Get install options for Marketo integration
export function getEloquaConfig(installId, token) {
  const actionText = 'Getting Eloqua Install Configuration Options';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.portal
        .getEloquaConfig({
          installId,
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const eloquaData = response.obj;
            dispatch(getEloquaConfigSuccess(eloquaData));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

export function getInstallMetaSuccess(meta) {
  return {
    type: actionTypes.GET_INSTALL_META_SUCCESS,
    meta,
  };
}

export function getInstallMeta(installId, token) {
  const actionText = 'Getting Marketo Install Configuration Options';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.portal
        .getObjectMeta({
          installId,
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const metaData = response.obj;
            dispatch(getInstallMetaSuccess(metaData));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}
